import React, { useEffect, useRef, useState } from 'react'

import { useDashboardV2Service } from 'features/dashboards-v2/service'
import { CommandPaletteView } from 'features/project/features/command-palette/components/CommandPaletteView/CommandPaletteView'
import { filterCommands } from 'features/project/features/command-palette/filter-commands'
import { useCommandPaletteCommands } from 'features/project/features/command-palette/hooks/use-command-palette-commands'
import { useEntityCommands } from 'features/project/features/command-palette/hooks/use-entity-commands'
import { useCommandPaletteStore } from 'features/project/features/command-palette/store'
import type { PaletteCommand } from 'features/project/features/command-palette/types'
import { useProjectStore } from 'features/project/store'
import { useSavedViewService } from 'features/saved-views/service'
import { useSavedViews } from 'features/saved-views/utils'
import { GLOBAL_SHORTCUT_KEYS } from 'hooks/keyboard-shortcuts'
import { useCurrentRoute } from 'hooks/use-current-route'
import { useEscapeDismiss } from 'hooks/use-escape-dismiss'
import { useKeyboardShortcuts } from 'hooks/use-keyboard-shortcuts'
import {
  trackPaletteCommandExecuted,
  trackPaletteOpened,
} from 'trackers/palette-events'
import type { ProjectState } from 'types/project'

type Props = {
  isProjectLocked?: boolean
}

export function CommandPaletteContainer({
  isProjectLocked = false,
}: Props): JSX.Element | null {
  const isOpen = useCommandPaletteStore((state) => state.isOpen)
  const closePalette = useCommandPaletteStore((state) => state.close)
  const togglePalette = useCommandPaletteStore((state) => state.toggle)
  const [query, setQuery] = useState('')
  const [focusedIndex, setFocusedIndex] = useState(0)

  const projectId = useProjectStore((state: ProjectState) => state.projectId)
  const { surface } = useCurrentRoute()
  const dashboardService = useDashboardV2Service()
  const savedViewService = useSavedViewService()
  const { updateFilters: applySavedView } = useSavedViews()
  const commands = useCommandPaletteCommands({ isProjectLocked })
  const entityCommands = useEntityCommands({
    dashboardService,
    savedViewService,
    applySavedView,
    enabled: isOpen,
  })
  const filtered = filterCommands([...commands, ...entityCommands], query)
  // The list can shrink under the cursor as the user types.
  const clampedIndex = Math.min(focusedIndex, Math.max(filtered.length - 1, 0))

  // Rising-edge reset + tracking: the palette can be opened from outside
  // this container (top-bar button, sidebar search), so open handling hangs
  // off the store state rather than a local open function.
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setQuery('')
      setFocusedIndex(0)
      trackPaletteOpened({ proj_uuid: projectId, surface })
    }
    wasOpenRef.current = isOpen
  }, [isOpen, projectId, surface])

  // respectInputs: false — unlike the layout's other global shortcuts, cmd+K
  // must open the palette even while a text field is focused.
  useKeyboardShortcuts(
    {
      [GLOBAL_SHORTCUT_KEYS.COMMAND_PALETTE_TOGGLE]: togglePalette,
      [GLOBAL_SHORTCUT_KEYS.COMMAND_PALETTE_TOGGLE_CTRL]: togglePalette,
    },
    { respectInputs: false, enabled: true },
  )

  useEscapeDismiss({ isOpen, onClose: closePalette })

  const handleSelect = (command: PaletteCommand, index: number): void => {
    closePalette()
    trackPaletteCommandExecuted({
      proj_uuid: projectId,
      surface,
      command_id: command.id,
      section: command.section,
      query_length: query.length,
      result_rank: index,
    })
    command.execute()
  }

  const handleQueryChange = (nextQuery: string): void => {
    setQuery(nextQuery)
    setFocusedIndex(0)
  }

  const handleInputKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setFocusedIndex(Math.min(clampedIndex + 1, filtered.length - 1))
        break
      case 'ArrowUp':
        event.preventDefault()
        setFocusedIndex(Math.max(clampedIndex - 1, 0))
        break
      case 'Enter': {
        event.preventDefault()
        const focusedCommand = filtered[clampedIndex]
        if (focusedCommand) handleSelect(focusedCommand, clampedIndex)
        break
      }
      case 'Tab':
        // Keep focus in the search input — arrows navigate the list.
        event.preventDefault()
        break
    }
  }

  if (!isOpen) return null

  return (
    <CommandPaletteView
      query={query}
      commands={filtered}
      focusedIndex={clampedIndex}
      onQueryChange={handleQueryChange}
      onInputKeyDown={handleInputKeyDown}
      onSelect={handleSelect}
      onDismiss={closePalette}
    />
  )
}
